<template>
  <CContainer class="datatable-container">
    <Loader :is-processing="isLoading" />
    <!-- Start Modals -->
    <!-- Unarchive message -->
    <CModal
      backdrop="static"
      :visible="!!showUnarchiveUsersModal"
      :close-on-backdrop="false"
    >
      <template #header-wrapper>
        <div
          class="pt-4 px-4 create-popup-header d-flex align-items-center justify-content-between"
        >
          <div
            class="d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal cursor-pointer"
            @click=";(showUnarchiveUsersModal = null), resetResponseMessages()"
          >
            <span class="close-icon" />
          </div>
        </div>
      </template>
      <div class="px-3 pt-3 pb-1">
        <div>
          {{ reqResponse.message }}
        </div>
        <div class="text-center mt-3">
          <button
            @click=";(showUnarchiveUsersModal = null), resetResponseMessages()"
            class="btn p-3 bg-gradient-blue text-white ms-2"
          >
            Okay
          </button>
        </div>
      </div>
      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
    <!-- End Modals -->
    <CRow class="justify-content-center mt-2">
      <CCol md="11" class="mb-5">
        <h1 class="text-center">
          Archived Users
          <HelpCenterButton _class="ml-2" content-key="archived_users_help" />
        </h1>
      </CCol>
    </CRow>

    <CRow class="mt-4 justify-content-center">
      <CCol md="11">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div
            v-for="(tab, index) in tableTabs"
            :key="index"
            class="table-tab"
            :class="{ active: tab.value === activeTableTab.value }"
          >
            {{ tab.label }}
          </div>
          <div class="actions d-flex align-items-center">
            <div
              class="lh-normal cursor-pointer text-accent-blue-light"
              @click="handleUsers(pagination.activePage, false, false, true)"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download CSV</span>
            </div>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search"
                  v-model="columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div
          class="status-guides-container pt-1 pb-2 d-flex align-items-center"
        >
          <div>
            <div v-if="!provideUsersByApiOnly">
              <div class="mb-2">Please select user(s) to unarchive</div>
              <div class="pl-2 d-flex align-items-center">
                <input
                  type="checkbox"
                  class="me-2"
                  @change="selectUsers($event)"
                />
                <button
                  class="px-2 py-2 bg-gradient-blue reverse text-white text-uppercase fw-bold"
                  id="archived-users-custom-btn"
                  @click="unArchiveBulk"
                >
                  Unarchive users
                </button>
              </div>
            </div>
          </div>
          <per-page-selector :action="setPerPage" :pagination="pagination" />
        </div>

        <!-- End of modals -->
        <CAlert
          v-if="reqResponse.message"
          class="mt-1 w-100"
          :color="reqResponse.type"
          :show="!!reqResponse.message"
        >
          {{ reqResponse.message }}
        </CAlert>

        <DataTable
          v-if="archivedUsers"
          class="datatable remove-first-th-border bg-white"
          :items="archivedUsers"
          :fields="dataTableFields"
          :column-filter="true"
          :sorter="{ external: true }"
          @update:sorter-value="sortByColumn"
        >
          <template #multiselect="{ item }">
            <td class="mw-auto">
              <input
                v-if="!provideUsersByApiOnly"
                v-model="selectedUsers"
                type="checkbox"
                :value="item.id"
              />
            </td>
          </template>

          <template #first_name-filter>
            <input
              v-model="columnSearchKeys.first_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #last_name-filter>
            <input
              v-model="columnSearchKeys.last_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #email-filter>
            <input
              v-model="columnSearchKeys.email.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #role_id-filter>
            <input
              v-model="columnSearchKeys.role_id.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #created_at-filter>
            <input
              v-model="columnSearchKeys.created_at.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #archived_date-filter>
            <input
              v-model="columnSearchKeys.archived_date.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #new>
            <td>
              <span
                class="px-3 py-2 rounded bg-gradient-blue text-white font-weight-bold"
                >Added</span
              >
            </td>
          </template>

          <template #action="{ item }">
            <td>
              <button
                v-if="!provideUsersByApiOnly"
                class="px-3 py-1 btn btn-unarchive d-flex align-items-center border-0"
                @click="unArchive(item)"
              >
                <span class="flaticon-back-arrow" />
                <span class="ml-1 text-uppercase font-weight-bold"
                  >Unarchive</span
                >
              </button>
            </td>
          </template>

          <template #role_id="{ item }">
            <td>
              {{
                item.current_role_in_school
                  ? item.current_role_in_school.name
                  : "N/A"
              }}
            </td>
          </template>
          <template #archived_date="{ item }">
            <td>
              <div
                v-html="
                  item.current_role_in_school &&
                  item.current_role_in_school.pivot
                    ? $helpers.getHTMLDateTime(
                        item.current_role_in_school.pivot.archived_at,
                        'MM/DD/YYYY',
                        'h:mm A',
                        '</br>'
                      )
                    : ''
                "
              />
            </td>
          </template>
          <template #created_at="{ item }">
            <td>
              <div
                v-html="
                  item.created_at
                    ? $helpers.getHTMLDateTime(
                        item.created_at,
                        'MM/DD/YYYY',
                        'h:mm A',
                        '</br>'
                      )
                    : ''
                "
              />
            </td>
          </template>
        </DataTable>

        <div v-else class="bg-white p-5 text-center text-dark">
          No Available Items.
        </div>

        <Pagination
          v-if="pagination.pages && pagination.pages > 1 && !isLazyLoadingMode"
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import Loader from "@/components/Loader"
import { mapGetters } from "vuex"
import PerPageSelector from "../PerPageSelector.vue"
import HelpCenterButton from "@/components/HelpCenterButton"

export default {
  name: "ArchivedUsersDataTable",
  components: {
    Loader,
    PerPageSelector,
    HelpCenterButton
  },
  data() {
    return {
      isLoading: false,
      isLazyLoadingMode: false,
      selectedUsers: [],
      showUnarchiveUsersModal: null,
      bulkUnarchiveSuccessMessage:
        "Your request to bulk unarchive has begun. If the action can be executed users will move from the Archived User page to the Main Users page. This will take some time to complete.",
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      tableTabs: [
        {
          label: "Archived Users",
          value: "archived_users"
        }
      ],
      activeTableTab: {
        value: "archived_users"
      },
      filterParams: {
        sort: "",
        searchQuery: ""
      },
      columnSearchKeys: {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"] },
        last_name: { value: "", column: ["last_name"] },
        email: { value: "", column: ["email"] },
        role_id: { value: "", column: ["role_user.name"] },
        created_at: { value: "", column: ["created_at"] },
        archived_date: { value: "", column: ["archived_date"] }
      },
      dataTableFields: [
        { key: "multiselect", label: "", sorter: false, filter: false },
        { key: "first_name", _style: "min-width:120px", label: "First Name" },
        { key: "last_name", _style: "min-width:120px", label: "Last Name" },
        { key: "email", label: "Email" },
        { key: "role_id", label: "Role" },
        { key: "created_at", _style: "min-width:140px", label: "Added Date" },
        {
          key: "archived_date",
          _style: "min-width:150px",
          label: "Archived Date"
        },
        { key: "action", label: "Action", sorter: false, filter: false }
      ]
    }
  },
  computed: {
    ...mapGetters({
      users: "users/archivedUsers",
      activeSchool: "schools/activeSchool"
    }),
    archivedUsers() {
      return this.users
    },
    provideUsersByApiOnly() {
      return (
        this.activeSchool && this.activeSchool.provide_users_by_api_only == 1
      )
    }
  },
  mounted() {
    this.initLazyLoading()
    this.handleUsers()
  },
  methods: {
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.users &&
            this.users.length &&
            this.pagination.total > this.users.length
          ) {
            if (body.offsetHeight + body.scrollTop >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.handleUsers(this.pagination.activePage, false, true)
            }
          }
        }
      }
    },
    toggleTab(tab) {
      this.tabs[tab] = !this.tabs[tab]
    },
    handleUsers(page, noLoader, isLazyLoadingMode, isExport) {
      this.pagination.activePage = page ? page : 1
      this.isLoading = !noLoader
      const params = {
        archived: true,
        page: this.pagination.activePage,
        per_page: this.pagination.per_page.value
      }
      if (this.filterParams.searchQuery) {
        params.search_query = this.filterParams.searchQuery
      }
      if (this.filterParams.sort_query) {
        params.sort = this.filterParams.sort_query
      }
      if (isExport) {
        this.exportUsers(params)
      } else {
        this.getUsers(params, isLazyLoadingMode)
      }
    },
    getUsers(params, isLazyLoadingMode) {
      this.$store
        .dispatch("users/getUsers", params)
        .then((response) => {
          const data = response.data
          if (isLazyLoadingMode) {
            this.$store.commit("users/PUSH_USERS", data.data)
          } else {
            this.$store.commit("users/SET_USERS", data.data)
          }
          const meta = response.data.meta
          if (meta) {
            this.pagination.total = meta.total
            this.pagination.from = meta.from
            this.pagination.to = meta.to
            this.pagination.pages = Math.ceil(meta.total / meta.per_page)
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    searchByQuery(value, type) {
      clearInterval(this.searchTimeOut)
      this.searchTimeOut = setTimeout(() => {
        this.handleQuerySearch(value, type)
      }, 500)
    },
    handleQuerySearch(value, type) {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        this.filterParams.searchQuery = ""
        if (type && type === "column") {
          this.columnSearchKeys.globalQuery = ""
          for (const key in this.columnSearchKeys) {
            if (Object.hasOwnProperty.call(this.columnSearchKeys, key)) {
              const columnData = this.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  this.filterParams.searchQuery =
                    this.filterParams.searchQuery +
                    `${col}:"${columnData.value}", `
                })
              }
            }
          }
          this.filterParams.searchQuery = this.filterParams.searchQuery.slice(
            0,
            -2
          )
        } else {
          this.resetSearchQuery()
          this.columnSearchKeys.globalQuery = query
          this.filterParams.searchQuery = query ? '"' + query + '"' : query
        }
        this.handleUsers()
      }
    },
    resetSearchQuery() {
      this.filterParams.searchQuery = ""
      this.columnSearchKeys = {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"] },
        last_name: { value: "", column: ["last_name"] },
        email: { value: "", column: ["email"] },
        role_id: { value: "", column: ["role_user.name"] },
        created_at: { value: "", column: ["created_at"] },
        archived_date: { value: "", column: ["archived_date"] }
      }
    },
    selectUsers(e) {
      if (e.target.checked) {
        this.selectedUsers = []
        this.users.forEach((user) => {
          this.selectedUsers.push(user.id)
        })
      } else {
        this.selectedUsers = []
      }
    },
    unArchive(user) {
      this.isLoading = true
      this.$store
        .dispatch("users/unArchive", user)
        .then(() => {
          setTimeout(() => {
            this.handleUsers(1, true)
            this.setSuccessResponse("Successfully unarchived!")
            this.isLoading = false
            this.showUnarchiveUsersModal = true
          }, 800)
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
          this.isLoading = false
          this.showUnarchiveUsersModal = true
        })
    },
    unArchiveBulk() {
      this.isLoading = true
      this.$store
        .dispatch("users/unArchiveBulk", { users: this.selectedUsers.join() })
        .then(() => {
          setTimeout(() => {
            this.handleUsers(1, true)
            this.setSuccessResponse(this.bulkUnarchiveSuccessMessage)
            this.isLoading = false
            this.showUnarchiveUsersModal = true
            this.isLoading = false
          }, 800)
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
          this.isLoading = false
          this.showUnarchiveUsersModal = true
        })
    },
    exportUsers(params) {
      this.isLoading = true
      params.per_page = [25, 50, 100].includes(this.pagination.per_page.value)
        ? this.pagination.per_page.value
        : "all"
      this.$store
        .dispatch("users/exportUsers", {
          ...params,
          is_archived: true,
          student_sis_id: true
        })
        .then((response) => {
          if (response.data) {
            this.$download.CSVExport(response.data, "archived_users")
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },

    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.pagination.per_page = option
        this.handleUsers()
      }
    },
    sortByColumn(column) {
      const columnName = this.columnSearchKeys[column.column]
        ? this.columnSearchKeys[column.column].column[0]
        : column.column
      this.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`
      this.handleUsers()
    },
    setActivePage(page) {
      this.pagination.activePage = page
      this.handleUsers(this.pagination.activePage)
      const topElement = document.getElementsByClassName(
        "datatable-container"
      )[0]
      topElement.scrollIntoView({ behavior: "smooth" })
    },
    resetResponseMessages() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    setSuccessResponse(message) {
      this.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    },
    setErrorResponse(message, errors) {
      this.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }
  }
}
</script>
